import React, { useState, useEffect } from 'react';
import { 
  Stack, 
  Text, 
  Group, 
  ActionIcon, 
  Button,
  Tooltip
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { DateInput } from '@mantine/dates';
import { IconPencil, IconDeviceFloppy, IconInfoCircle, IconX } from '@tabler/icons-react';
import { ImageDetails } from '../../../../../types/Image';
import { 
  parseExifDate, 
  formatToExifDate, 
  formatDateForDisplay,
  formatTimeString
} from '../../../../../utils/dateUtils';

interface DatetimeSectionProps {
  datetime: string | null;
  eventDate?: Date;
  onSave: (isoDateString: string) => void;
  onSaveEventDate?: (newDate: Date) => void;
}
export const DatetimeSection: React.FC<DatetimeSectionProps> = ({ 
  datetime, 
  eventDate,
  onSave,
  onSaveEventDate
}) => {
  const [editing, setEditing] = useState(false);
  // Parse the ISO datetime string from metadata
  const [dateValue, setDateValue] = useState<Date | null>(null);
  const [timeValue, setTimeValue] = useState<string | null>(null);
  // Track whether the current date comes from an event
  const [isUsingEventDate, setIsUsingEventDate] = useState(false);
  
  // Initialize date and time values from datetime prop or eventDate
  useEffect(() => {
    // Handle both null and undefined values
    const hasImageDateTime = datetime !== null && datetime !== undefined;
    const hasEventDate = eventDate !== null && eventDate !== undefined;
    
    // Prioritize event date over image date
    if (hasEventDate) {
      // If event has date, use it
      const date = new Date(eventDate);
      
      // If image has datetime, extract the time from it
      if (hasImageDateTime) {
        try {
          const imageDate = parseExifDate(datetime);
          if (imageDate) {
            // Keep event date but use image time
            date.setHours(imageDate.getHours(), imageDate.getMinutes(), imageDate.getSeconds(), 0);
          }
        } catch (error) {
          console.error('Error extracting time from image date:', error);
          // Default to 9:00 AM if we can't extract time
          date.setHours(9, 0, 0, 0);
        }
      } else {
        // No image datetime, default to 9:00 AM
        date.setHours(9, 0, 0, 0);
      }
      
      setDateValue(date);
      setTimeValue(formatTimeString(date));
      setIsUsingEventDate(true);
      console.log('Using event date:', date);
    } else if (hasImageDateTime) {
      // If no event date but image has datetime, use image datetime
      try {
        const date = parseExifDate(datetime);
        
        if (!date) {
          setDateValue(null);
          setTimeValue(null);
          setIsUsingEventDate(false);
          return;
        }
        
        setDateValue(date);
        setTimeValue(formatTimeString(date));
        setIsUsingEventDate(false);
      } catch (error) {
        console.error('Error parsing datetime:', error);
        setDateValue(null);
        setTimeValue(null);
        setIsUsingEventDate(false);
      }
    } else {
      // No event date and no image datetime
      setDateValue(null);
      setTimeValue(null);
      setIsUsingEventDate(false);
    }
  }, [datetime, eventDate]);
  
  // Save the datetime in EXIF format
  const saveDatetime = () => {
    if (dateValue) {
      try {
        const date = new Date(dateValue);
        
        // Apply time value if available
        if (timeValue) {
          const [hours, minutes] = timeValue.split(':').map(Number);
          date.setHours(hours || 0);
          date.setMinutes(minutes || 0);
          date.setSeconds(0);
        }
        
        // Handle both null and undefined values
        const hasImageDateTime = datetime !== null && datetime !== undefined;
        const hasEventDate = eventDate !== null && eventDate !== undefined;
        
        // Determine whether to update image datetime, event date, or both
        if (isUsingEventDate && onSaveEventDate) {
          // If using event date, update event date with new time only
          const updatedEventDate = new Date(eventDate!);
          updatedEventDate.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
          console.log('Updating event date time only:', updatedEventDate);
          onSaveEventDate(updatedEventDate);
        } else {
          // Otherwise update image datetime
          const exifDateString = formatToExifDate(date);
          console.log('Saving datetime in EXIF format:', exifDateString);
          onSave(exifDateString);
        }
        
        setEditing(false);
      } catch (error) {
        console.error('Error saving datetime:', error);
      }
    }
  };

  return (
    (editing? 
      <Stack gap='0'>
        {isUsingEventDate && (
          <Tooltip
            label="This image is part of an event. The date is determined by the event."
            position="top"
            withArrow
          >
            <Text size="xs" c="dimmed" fw={500} style={{marginBottom: '4px', display: 'flex', alignItems: 'center'}}>
              Using event date <IconInfoCircle size={14} style={{ marginLeft: 5, opacity: 0.7 }} />
            </Text>
          </Tooltip>
        )}
      
      <Group gap="5px" justify="space-between" style={{flex: 1}}>
          <DateInput
            size="sm"
            placeholder="Date"
            value={dateValue}
            onChange={setDateValue}
            style={{flex: 1.7}} /* Make date input wider than time input */
            disabled={isUsingEventDate}
          />
        <TimeInput
          size="sm"
          placeholder="Time"
          value={timeValue || ''}
          onChange={(e) => setTimeValue(e.currentTarget.value || null)}
          style={{flex: 1}} /* Time input is narrower */
        />
        <ActionIcon size="lg" variant="filled" color="blue" onClick={saveDatetime}>
          <IconDeviceFloppy size={16}/>
        </ActionIcon>
      </Group>
      </Stack>
     : 
      <Group justify={'space-between'}>
        <Text size="sm">
          {dateValue ? formatDateForDisplay(dateValue.toISOString()) : 'No date available'}
        </Text>
        <ActionIcon size="lg" variant="subtle" onClick={() => setEditing(true)}>
          <IconPencil size={16}/>
        </ActionIcon>
      </Group>
    )
  );
};
